import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { LogOut, Menu, ShieldCheck, Ticket, X } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { login } from "@/lib/api";

const LINKS = [
  ["/flights", "Flights"],
  ["/hotels", "Hotels"],
  ["/cabs", "Taxi"],
  ["/packages", "Destinations"],
  ["/planner", "AI Itinerary"],
];

export const Logo = ({ light }) => (
  <Link to="/" className={`flex items-center ${light ? "w-fit rounded-xl bg-white p-2" : ""}`} data-testid="brand-logo">
    <img src="/logo.jpg?v=2" alt="NextVisionSolutions — Your global journey, our guidance" className={`${light ? "h-20" : "h-16"} w-auto object-contain mix-blend-multiply`} />
  </Link>
);

const UserMenu = () => {
  const { user, logout } = useAuth();
  if (!user)
    return (
      <button onClick={() => login()} data-testid="nav-login-btn"
        className="rounded-full bg-[#0D1B2A] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#E07A5F]">
        Sign in
      </button>
    );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="nav-user-menu" className="flex items-center gap-2 rounded-full border border-[#E5E0D8] bg-white py-1 pl-1 pr-3">
        {user.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" className="h-8 w-8 rounded-full object-cover bg-[#F4F0EA]" />
          : <span className="grid h-8 w-8 place-items-center rounded-full bg-[#E07A5F] text-sm font-bold text-white">{user.name?.[0] || "U"}</span>}
        <span className="hidden text-sm font-medium sm:block">{user.name.split(" ")[0]}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem asChild><Link to="/my-bookings" data-testid="nav-my-bookings"><Ticket className="mr-2 h-4 w-4" />My bookings</Link></DropdownMenuItem>
        {user.is_admin && (
          <DropdownMenuItem asChild><Link to="/admin" data-testid="nav-admin"><ShieldCheck className="mr-2 h-4 w-4" />Admin panel</Link></DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={logout} data-testid="nav-logout"><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const cls = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-[#E07A5F] ${isActive ? "text-[#E07A5F]" : "text-[#1B263B]"}`;
  return (
    <header className="sticky top-0 z-50 border-b border-[#E5E0D8]/70 bg-[#FDFBF7]/85 backdrop-blur-md">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-8">
        <Logo />
        <nav className="hidden items-center gap-7 lg:flex">
          {LINKS.map(([to, label]) => (
            <NavLink key={to} to={to} className={cls} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}>{label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <span className="hidden rounded-full border border-[#E5E0D8] px-3 py-1 text-xs font-semibold sm:block" data-testid="currency-indicator">₹ INR</span>
          <UserMenu />
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="flex flex-col gap-4 border-t border-[#E5E0D8] px-6 py-5 lg:hidden">
          {LINKS.map(([to, label]) => (
            <NavLink key={to} to={to} className={cls} onClick={() => setOpen(false)} data-testid={`mnav-${label.toLowerCase().replace(/\s/g, "-")}`}>{label}</NavLink>
          ))}
        </nav>
      )}
    </header>
  );
};
